import { useEffect, useRef, useState } from "react";

const connections = new Set<() => void>();
/** Stop old callbacks and reconnect timers synchronously when a workspace changes. */
export function disconnectWebSockets(): void {
  for (const disconnect of [...connections]) disconnect();
}

/**
 * Phase 1 minimal WebSocket hook. Auto-reconnects with exponential backoff.
 *
 * In dev, vite proxies `/ws` -> ws://localhost:8787.
 *
 * The shape of incoming/outgoing payloads is JSON-encoded
 * `{ type: string, ... }` — concrete schema is owned by chat-api.
 */
export interface WsMessage<T = unknown> {
  type: string;
  payload?: T;
  [k: string]: unknown;
}

export type WsState = "idle" | "connecting" | "open" | "closed" | "error";

export interface UseWsOptions {
  /** Provide token; falls back to localStorage. */
  token?: string | null;
  /** Disable connection (e.g. unauthenticated). */
  enabled?: boolean;
  /** Called for every parsed message. */
  onMessage?: (msg: WsMessage) => void;
  onOpen?: () => void | Promise<void>;
  onDenied?: () => void;
  /** When set, send subprotocols instead of putting the token on the URL. */
  authProtocol?: string;
}

export interface UseWsResult {
  state: WsState;
  send: (msg: WsMessage) => boolean;
  /** Last message received (for declarative consumers). */
  lastMessage: WsMessage | null;
}

export function useWebSocket(
  url: string,
  options: UseWsOptions = {},
): UseWsResult {
  const { token, enabled = true, onMessage, onOpen, onDenied, authProtocol } = options;
  const [state, setState] = useState<WsState>("idle");
  const [lastMessage, setLastMessage] = useState<WsMessage | null>(null);
  const ref = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onDeniedRef = useRef(onDenied); onDeniedRef.current = onDenied;

  useEffect(() => {
    if (!enabled) {
      setState("idle");
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let lastEventId = 0;

    const connect = () => {
      if (cancelled) return;
      setState("connecting");
      const t =
        token ??
        (typeof localStorage !== "undefined"
          ? localStorage.getItem("ailechat:token")
          : null);
      const address = new URL(url);
      let ws: WebSocket;
      try {
        if (authProtocol) {
          address.searchParams.delete("token");
          ws = new WebSocket(address.toString(), [authProtocol, `aile-auth.${t ?? ""}`]);
        } else {
          if (t) address.searchParams.set("token", t);
          ws = new WebSocket(address.toString());
        }
      } catch (e) {
        setState("error");
        scheduleReconnect();
        return;
      }
      ref.current = ws;

      ws.onopen = () => {
        if (cancelled) { ws.close(); return; }
        attempt = 0;
        setState("open");
        if (lastEventId) ws.send(JSON.stringify({ type: "resume", payload: { last_event_id: lastEventId } }));
        void onOpenRef.current?.();
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25_000);
      };
      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(typeof ev.data === "string" ? ev.data : "{}") as WsMessage;
          if (typeof data.event_id === "number") lastEventId = Math.max(lastEventId, data.event_id);
          setLastMessage(data);
          onMessageRef.current?.(data);
        } catch {
          // ignore malformed
        }
      };
      ws.onerror = () => {
        setState("error");
      };
      ws.onclose = (event) => {
        if (heartbeat) clearInterval(heartbeat);
        if (cancelled) return;
        setState("closed");
        if ((event?.code === 4003 || event?.code === 1008) && onDeniedRef.current) { onDeniedRef.current(); return; }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      attempt += 1;
      const delay = Math.min(30_000, 500 * 2 ** attempt);
      reconnectTimer = setTimeout(connect, delay);
    };

    const disconnect = () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeat) clearInterval(heartbeat);
      if (ref.current && ref.current.readyState <= WebSocket.OPEN) {
        ref.current.close();
      }
      ref.current = null;
      connections.delete(disconnect);
    };
    connections.add(disconnect);
    connect();
    return disconnect;
  }, [url, token, enabled, authProtocol]);

  const send = (msg: WsMessage): boolean => {
    const ws = ref.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  };

  return { state, send, lastMessage };
}
